// ✅ src/pages/VehicleV2/HireVehicleReport.jsx
// UPDATED FULL FILE — production listing design template
//
// Expected endpoints:
//   GET /api/hireVehicleReport/dropdowns?branchId=&branchType=&loginUserId=
//   GET /api/hireVehicleReport/subtypes?vehTypeId=
//   GET /api/hireVehicleReport/list?branchId=&branchType=&loginUserId=&branch=&store=&vehicleMainType=&vehicleSubType=&vehicleNumber=&hiredFrom=&page=&pageSize=
//   GET /api/hireVehicleReport/autocomplete/vehicleNumber?...
//   GET /api/hireVehicleReport/autocomplete/hiredFrom?...

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaPrint,
  FaEye,
  FaArrowLeft,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/hireVehicleReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
  }));
  return list.length
    ? list
    : [{ label: fallbackLabel, value: fallbackLabel }];
};

const realOptions = (arr) =>
  toList(arr).filter((x) => !isSelect(x?.value) && !isSelect(x?.label));

const defaultFilters = {
  branch: "Select",
  store: "Select",
  vehicleMainType: "Select",
  vehicleSubType: "Select",
  vehicleNumber: "",
  hiredFrom: "",
};

const mapRow = (r) => {
  const get = (...keys) => {
    for (const k of keys) {
      if (r && r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "") {
        return r[k];
      }
    }
    return "";
  };

  return {
    raw: r,
    vehicleId: get("fld_vehicleid", "vehicleId", "VehicleId", "vehid", "id"),
    vehicleMainType: get(
      "fld_vehicletypename",
      "vehicleMainType",
      "VehicleMainType",
      "vehicletype",
      "mainType"
    ),
    vehicleCode: get("fld_vehicle_code", "vehicleCode", "VehicleCode", "code"),
    vehicleNumber: get(
      "fld_vehicle_regno",
      "vehicleNumber",
      "VehicleNumber",
      "regNo"
    ),
    vehicleName: get("fld_vehicle_name", "vehicleName", "VehicleName", "name"),
    branch: get("fld_branchname", "branch", "BranchName"),
    vehicleType: get(
      "fld_vehicle_subtypename",
      "vehicleType",
      "VehicleType",
      "subTypeName"
    ),
    hiredFrom: get("fld_hiredfrom", "hiredFrom", "HiredFrom"),
  };
};

export default function HireVehicleReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);
  const vehNoTimer = useRef(null);
  const hiredTimer = useRef(null);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [subTypeLoading, setSubTypeLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);

  const isBusy =
    pageLoading ||
    dropdownLoading ||
    listLoading ||
    subTypeLoading ||
    autoLoading;

  /* ===========================
     ui states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     dropdown states
  =========================== */
  const [branches, setBranches] = useState([
    { label: "Select", value: "Select" },
  ]);
  const [stores, setStores] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([
    { label: "Select", value: "Select" },
  ]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([
    { label: "Select", value: "Select" },
  ]);
  const [allSubTypes, setAllSubTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  /* ===========================
     autocomplete states
  =========================== */
  const [vehNoSug, setVehNoSug] = useState([]);
  const [hiredSug, setHiredSug] = useState([]);

  /* ===========================
     computed
  =========================== */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const suggestBoxClass = useMemo(
    () =>
      `absolute z-20 mt-1 w-full rounded-lg border shadow-lg max-h-48 overflow-auto ${
        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
      }`,
    [isDark]
  );

  const suggestItemClass = useMemo(
    () =>
      `px-3 py-2 cursor-pointer text-sm ${
        isDark
          ? "hover:bg-gray-800 text-gray-200"
          : "hover:bg-gray-100 text-gray-800"
      }`,
    [isDark]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const clearAutoTimers = useCallback(() => {
    if (vehNoTimer.current) {
      clearTimeout(vehNoTimer.current);
      vehNoTimer.current = null;
    }
    if (hiredTimer.current) {
      clearTimeout(hiredTimer.current);
      hiredTimer.current = null;
    }
  }, []);

  /* ===========================
     dropdown load
  =========================== */
  const loadDropdowns = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: {
          branchId: session.branchId,
          branchType: session.branchType,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load dropdowns");
        return;
      }

      setBranches(normalizeOptionList(data.branches, "Select"));
      setStores(normalizeOptionList(data.stores, "Select"));
      setVehicleMainTypes(normalizeOptionList(data.vehicleMainTypes, "Select"));

      const nextSubTypes = normalizeOptionList(data.vehicleSubTypes, "Select");
      setAllSubTypes(nextSubTypes);
      setVehicleSubTypes(nextSubTypes);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load dropdowns"
      );
    } finally {
      setDropdownLoading(false);
    }
  }, [session, commonHeaders]);

  /* ===========================
     subtype load
  =========================== */
  const loadSubTypes = useCallback(
    async (vehTypeId) => {
      const id = toInt(vehTypeId, 0);

      if (id <= 0) {
        setVehicleSubTypes(allSubTypes);
        return;
      }

      try {
        setSubTypeLoading(true);

        const { data } = await axios.get(`${API_BASE}/subtypes`, {
          params: { vehTypeId: id },
          headers: commonHeaders,
        });

        const next = normalizeOptionList(data?.vehicleSubTypes, "Select");

        // /subtypes often returns only Select when that main type has no mapped rows
        if (data?.success && realOptions(next).length) {
          setVehicleSubTypes(next);
        } else {
          setVehicleSubTypes(allSubTypes);
        }
      } catch (err) {
        console.error(err);
        setVehicleSubTypes(allSubTypes);
      } finally {
        setSubTypeLoading(false);
      }
    },
    [commonHeaders, allSubTypes]
  );

  /* ===========================
     list load
  =========================== */
  const loadList = useCallback(
    async (nextPage = 1, nextFilters = filters) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchId: session.branchId,
            branchType: session.branchType,
            loginUserId: session.loginUserId,
            branch: nextFilters.branch,
            store: nextFilters.store,
            vehicleMainType: nextFilters.vehicleMainType,
            vehicleSubType: nextFilters.vehicleSubType,
            vehicleNumber: nextFilters.vehicleNumber,
            hiredFrom: nextFilters.hiredFrom,
            page: nextPage,
            pageSize,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "List failed");
        }

        const mapped = toList(data.rows).map(mapRow);
        setRows(mapped);
        setTotal(toInt(data.total, 0));
        setPage(toInt(data.page, nextPage));
      } catch (err) {
        console.error(err);
        setRows([]);
        setTotal(0);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setListLoading(false);
      }
    },
    [filters, pageSize, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadDropdowns();
      await loadList(1, defaultFilters);
    } finally {
      setPageLoading(false);
    }
  }, [loadDropdowns, loadList]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "vehicleMainType") {
        next.vehicleSubType = "Select";
      }

      return next;
    });
  }, []);

  const handleSearch = useCallback(async () => {
    clearAutoTimers();
    setVehNoSug([]);
    setHiredSug([]);
    await loadList(1, filters);
  }, [clearAutoTimers, loadList, filters]);

  const handleViewAll = useCallback(async () => {
    clearAutoTimers();
    setVehNoSug([]);
    setHiredSug([]);
    setFilters(defaultFilters);
    await loadList(1, defaultFilters);
    toast.info("Showing all records");
  }, [clearAutoTimers, loadList]);

  const handleRefresh = useCallback(async () => {
    clearAutoTimers();
    setVehNoSug([]);
    setHiredSug([]);
    await loadPage();
  }, [clearAutoTimers, loadPage]);

  const handlePrintAll = useCallback(() => {
    const params = new URLSearchParams({
      branchId: String(session.branchId),
      branchType: String(session.branchType),
      loginUserId: String(session.loginUserId),
      branch: filters.branch,
      store: filters.store,
      vehicleMainType: filters.vehicleMainType,
      vehicleSubType: filters.vehicleSubType,
      vehicleNumber: filters.vehicleNumber,
      hiredFrom: filters.hiredFrom,
      page: String(page),
      pageSize: String(pageSize),
    });

    navigate(`/vehicle/HireVehicleReport_AllPrint?${params.toString()}`);
  }, [navigate, session, filters, page, pageSize]);

  const handlePrintRow = useCallback(
    (row) => {
      const vehicleId = row?.vehicleId;
      if (!vehicleId) {
        toast.error("Vehicle Id not found in this row");
        return;
      }

      navigate(`/vehicle/HireVehicleReport_Print?vehicleId=${vehicleId}`);
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goPage = useCallback(
    async (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      await loadList(next, filters);
    },
    [filters, totalPages, loadList]
  );

  /* ===========================
     autocomplete handlers
  =========================== */
  const onVehicleNumberChange = useCallback(
    (e) => {
      const v = e.target.value;
      setField("vehicleNumber", v);

      if (vehNoTimer.current) clearTimeout(vehNoTimer.current);

      if (!v || v.length < 2) {
        setVehNoSug([]);
        return;
      }

      vehNoTimer.current = setTimeout(async () => {
        try {
          setAutoLoading(true);

          const { data } = await axios.get(
            `${API_BASE}/autocomplete/vehicleNumber`,
            {
              params: {
                branchId: session.branchId,
                branchType: session.branchType,
                loginUserId: session.loginUserId,
                prefix: v,
                vehTypeIndex: isSelect(filters.vehicleMainType)
                  ? "Select"
                  : filters.vehicleMainType,
                subTypeId: isSelect(filters.vehicleSubType)
                  ? "Select"
                  : filters.vehicleSubType,
                store: isSelect(filters.store) ? "Select" : filters.store,
              },
              headers: commonHeaders,
            }
          );

          if (data?.success) {
            setVehNoSug(toList(data.list));
          } else {
            setVehNoSug([]);
          }
        } catch (err) {
          console.error(err);
          setVehNoSug([]);
        } finally {
          setAutoLoading(false);
        }
      }, 300);
    },
    [setField, session, filters, commonHeaders]
  );

  const onHiredFromChange = useCallback(
    (e) => {
      const v = e.target.value;
      setField("hiredFrom", v);

      if (hiredTimer.current) clearTimeout(hiredTimer.current);

      if (!v || v.length < 2) {
        setHiredSug([]);
        return;
      }

      hiredTimer.current = setTimeout(async () => {
        try {
          setAutoLoading(true);

          const { data } = await axios.get(
            `${API_BASE}/autocomplete/hiredFrom`,
            {
              params: {
                branchId: session.branchId,
                branchType: session.branchType,
                loginUserId: session.loginUserId,
                prefix: v,
                vehTypeIndex: isSelect(filters.vehicleMainType)
                  ? "Select"
                  : filters.vehicleMainType,
                store: isSelect(filters.store) ? "Select" : filters.store,
              },
              headers: commonHeaders,
            }
          );

          if (data?.success) {
            setHiredSug(toList(data.list));
          } else {
            setHiredSug([]);
          }
        } catch (err) {
          console.error(err);
          setHiredSug([]);
        } finally {
          setAutoLoading(false);
        }
      }, 300);
    },
    [setField, session, filters, commonHeaders]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const maybeId = toInt(filters.vehicleMainType, 0);

    if (maybeId > 0) {
      loadSubTypes(maybeId);
    } else {
      setVehicleSubTypes(allSubTypes);
    }
  }, [filters.vehicleMainType, loadSubTypes, allSubTypes]);

  useEffect(() => {
    return () => {
      clearAutoTimers();
    };
  }, [clearAutoTimers]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">HIRE VEHICLE REPORT</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Hire Vehicle Report
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {session.branchId || "-"} • User ID:{" "}
                {session.loginUserId || "-"} • Records: {total} • Page: {page}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handlePrintAll}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaPrint />
                Print
              </button>

              <button
                type="button"
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                type="button"
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && !pageLoading && !dropdownLoading && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {(branches || []).map((b, idx) => (
                      <option key={`${b.value}-${idx}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {(stores || []).map((s, idx) => (
                      <option key={`${s.value}-${idx}`} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {(vehicleMainTypes || []).map((t, idx) => (
                      <option key={`${t.value}-${idx}`} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Sub Type</label>
                  <select
                    name="vehicleSubType"
                    value={filters.vehicleSubType}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy || isSelect(filters.vehicleMainType)}
                  >
                    {(vehicleSubTypes || []).map((t, idx) => (
                      <option key={`${t.value}-${idx}`} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={onVehicleNumberChange}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                    autoComplete="off"
                    disabled={isBusy}
                  />
                  {vehNoSug.length > 0 && (
                    <div className={suggestBoxClass}>
                      {vehNoSug.slice(0, 20).map((s, idx) => (
                        <div
                          key={`${s}-${idx}`}
                          onClick={() => {
                            setField("vehicleNumber", s);
                            setVehNoSug([]);
                          }}
                          className={suggestItemClass}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className={labelClass}>Hired From</label>
                  <input
                    type="text"
                    name="hiredFrom"
                    value={filters.hiredFrom}
                    onChange={onHiredFromChange}
                    className={inputClass}
                    placeholder="Enter hired from"
                    autoComplete="off"
                    disabled={isBusy}
                  />
                  {hiredSug.length > 0 && (
                    <div className={suggestBoxClass}>
                      {hiredSug.slice(0, 20).map((s, idx) => (
                        <div
                          key={`${s}-${idx}`}
                          onClick={() => {
                            setField("hiredFrom", s);
                            setHiredSug([]);
                          }}
                          className={suggestItemClass}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : "Loading..."}
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      S.No
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Main Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Code
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Number
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Branch
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Hired From
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      Print
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  } divide-y`}
                >
                  {rows.map((row, idx) => (
                    <tr
                      key={`${row.vehicleId}-${idx}`}
                      className={`${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                      }`}
                    >
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.vehicleMainType || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {row.vehicleCode || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.vehicleNumber || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {row.vehicleName || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.branch || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.vehicleType || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.hiredFrom || "-"}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => handlePrintRow(row)}
                          className={`text-lg transition ${
                            isDark
                              ? "text-gray-300 hover:text-white"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                          title="Print"
                        >
                          <FaPrint className="inline w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {total > 0 && totalPages > 1 && !isBusy && (
            <div className="mt-6 flex justify-center items-center gap-3">
              <button
                type="button"
                onClick={() => goPage(page - 1)}
                disabled={page <= 1}
                className={`px-3 py-1 border rounded text-sm ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                Prev
              </button>

              <span
                className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}
              >
                Page <b>{page}</b> / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => goPage(page + 1)}
                disabled={page >= totalPages}
                className={`px-3 py-1 border rounded text-sm ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                Next
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}